import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import FormMonitoring from 'components/FormMonitoring';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppColors } from 'resources/AppColors';
import CustomDialog from 'components/Dialogs/MasterDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import FileInput from 'components/FileInput';
import {
  createEmptyStateLexical,
  encodeImageFileAsURL,
} from 'utils/HelperFunctions';
import { CommonTexts, ContentFormsLabels, contentTexts } from 'locale/es';
import RichTextEditorLexical from 'components/RichTextEditor/Lexical';
import SingleChoiceDropdown from 'components/Dropdowns/SingleChoiceDropdown';
import { initializeBusinessPackageOptions } from 'redux/reducers/dataAppAndPackageReducer';
import { z } from 'zod';
import { Controller, useController, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

const useStyles = makeStyles((theme) => ({
  textFieldLabel: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: AppColors.PRIMARY,
      },
    },
    '& .MuiInputBase-root': {
      color: AppColors.PRIMARY,
    },
    '& .MuiInputLabel-root': {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
}));

const fullScreenOptions = [
  { label: 'Si', value: true },
  { label: 'No', value: false },
];
const featuredOptions = [
  { label: 'Si', value: true },
  { label: 'No', value: false },
];

export const dropdownOptions = [
  {
    name: contentTexts.DROPDOWNOPTIONS.VIDEO.name,
    value: contentTexts.DROPDOWNOPTIONS.VIDEO.value,
  },
  {
    name: contentTexts.DROPDOWNOPTIONS.IMAGE.name,
    value: contentTexts.DROPDOWNOPTIONS.IMAGE.value,
  },
  {
    name: contentTexts.DROPDOWNOPTIONS.DOCUMENT.name,
    value: contentTexts.DROPDOWNOPTIONS.DOCUMENT.value,
  },
  {
    name: contentTexts.DROPDOWNOPTIONS.WEB.name,
    value: contentTexts.DROPDOWNOPTIONS.WEB.value,
  },
];

function textEditorSchema() {
  return z.object({
    jsonTextEditor: z.any(),
    rawHtmlTextEditor: z.string(),
    plainTextEditor: z.string(),
  });
}

// * Optional field with a minimum length of 2 characters if input exists
const labelType = z.string().superRefine((val, ctx) => {
  if (val.length === 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 2,
      type: 'string',
      inclusive: true,
      message: contentTexts.formInputErrors.label_min,
    });
  }

  if (val.length > 30) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_big,
      maximum: 30,
      type: 'string',
      inclusive: true,
      message: contentTexts.formInputErrors.label_max,
    });
  }

  return val;
});

const NewContentSchema = z
  .object({
    title: z
      .string()
      .min(2, contentTexts.formInputErrors.title_min)
      .max(100, contentTexts.formInputErrors.title_max),
    description: textEditorSchema(),
    author: z.string().optional(),
    link: z.string().optional(),
    notificationText: z.string().optional(),
    styledNotificationText: textEditorSchema(),
    contentType: z.string().min(1, contentTexts.formInputErrors.contentType),
    label: labelType,
    businessPackage: z.string().optional(),
    packageLabel: z.string().optional(),
    image: z.any(),
    imageContentType: z.string().optional(),
    fullScreen: z.boolean(),
    featured: z.boolean(),
  })
  .refine((data) => data.link || data.description.plainTextEditor.trim(), {
    message: contentTexts.formInputErrors.descriptionOrLink,
    path: ['link'],
  })
  .refine((data) => data.link || data.description.plainTextEditor.trim(), {
    message: contentTexts.formInputErrors.descriptionOrLink,
    path: ['description.jsonTextEditor'],
  })
  .transform((data) => ({
    ...data,
    description: data.description.rawHtmlTextEditor,
    styledNotificationText: data.styledNotificationText.rawHtmlTextEditor,
    enabled: true,
    businessPackage: {
      id: data.businessPackage,
    },
  }));

function ContentForm({ onSubmit, isEdit = false }) {
  const { entity } = useLocation().state;

  const selectedCenter = useSelector(({ center }) => center)?.data;

  const businessCenter = selectedCenter.businessPackage.id

  const MAX_LENGTH_TITLE = 100;
  const MAX_LENGTH_LABEL = 30;

  const { handleSubmit, control, reset, getValues } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: zodResolver(NewContentSchema),
    resetOptions: {
      keepDefaultValues: true,
    },
    defaultValues: {
      title: entity?.title || '',
      description: {
        jsonTextEditor: createEmptyStateLexical(),
        rawHtmlTextEditor: '',
        plainTextEditor: '',
      },
      author: entity?.author || '',
      link: entity?.link || '',
      notificationText: entity?.notificationText || '',
      styledNotificationText: {
        jsonTextEditor: createEmptyStateLexical(),
        rawHtmlTextEditor: '',
        plainTextEditor: '',
      },
      contentType: entity?.contentType || '',
      label: entity?.label || '',
      businessPackage: entity?.businessPackage?.id || businessCenter || '',
      packageLabel: entity?.packageLabel || '',
      image: entity?.image || undefined,
      imageContentType: entity?.imageContentType || '',
      fullScreen: entity?.fullScreen || false,
      featured: entity?.featured || false,
    },
  });

  const {
    field: { value: descriptionJSON, onChange: changeDescriptionJSON },
    fieldState: { error: descriptionJSONError },
  } = useController({
    name: 'description.jsonTextEditor',
    control,
  });

  const {
    field: { value: descriptionHTML, onChange: changeDescriptionHTML },
  } = useController({
    name: 'description.rawHtmlTextEditor',
    control,
  });

  const {
    field: { value: descriptionPlain, onChange: changeDescriptionPlain },
  } = useController({
    name: 'description.plainTextEditor',
    control,
  });

  const {
    field: { value: notificationJSON, onChange: changeNotificationJSON },
  } = useController({
    name: 'styledNotificationText.jsonTextEditor',
    control,
  });

  const {
    field: { value: notificationHTML, onChange: changeNotificationHTML },
  } = useController({
    name: 'styledNotificationText.rawHtmlTextEditor',
    control,
  });

  const {
    field: { value: notificationPlain, onChange: changeNotificationPlain },
  } = useController({
    name: 'styledNotificationText.plainTextEditor',
    control,
  });

  const {
    field: { value: image, onChange: changeImage },
  } = useController({
    name: 'image',
    control,
  });

  const {
    field: { value: imageContentType, onChange: changeImageContentType },
  } = useController({
    name: 'imageContentType',
    control,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [resetImage, setResetImage] = useState(false);
  const [formError, setFormError] = useState(undefined);
  const [urlImage] = useState(entity?.url || null);

  const resetDescriptionRef = useRef();
  const resetStyledNotificationRef = useRef();

  const { businessPackageOptions } = useSelector(
    ({ optionsAppAndBusiness }) => optionsAppAndBusiness
  );

  useEffect(() => {
    if (!businessPackageOptions) {
      dispatch(initializeBusinessPackageOptions());
    }
  }, [businessPackageOptions, dispatch]);

  useEffect(() => {
    if (entity?.description) {
      changeDescriptionHTML(entity?.description);
    }
  }, [entity?.description]);

  useEffect(() => {
    if (entity?.styledNotificationText) {
      changeNotificationHTML(entity?.styledNotificationText);
    }
  }, [entity?.styledNotificationText]);

  const classes = useStyles();

  async function handleOnSubmit(data) {
    try {
      const contentObj = {
        ...data,
        [isEdit ? 'id' : 'centers']: isEdit ? entity.id : [{ id: entity.id }],
      };

      await dispatch(onSubmit(contentObj));
      handleShowAddModal();
    } catch (error) {
      setFormError(error);
    }
  }

  function resetFormInputs() {
    resetDescriptionRef.current.click();
    resetStyledNotificationRef.current.click();
    resetImageAction();
    reset();
  }

  function resetImageAction() {
    setResetImage(!resetImage);
  }

  function handleImage(e) {
    changeImageContentType(e.target.value.type);
    encodeImageFileAsURL(e.target.value, (result) => {
      changeImage(result?.replace(/^data:image\/[a-z]+;base64,/, ''));
    });
  }

  function handleShowAddModal() {
    navigate(-1);
  }

  if (!businessPackageOptions) return null;

  return (
    <CustomDialog
      title={isEdit ? contentTexts.EDIT_TITLE : contentTexts.CREATED_TITLE}
      show={true}
      handleShow={handleShowAddModal}
    >
      <FormMonitoring
        submitForm={handleSubmit((data) => handleOnSubmit(data))}
        resetFormInputs={resetFormInputs}
        isEdit={isEdit}
      >
        <Controller
          control={control}
          name='title'
          render={({
            field: { value, onChange, onBlur, ref },
            fieldState: { error },
          }) => (
            <>
              <TextField
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                inputRef={ref}
                type='text'
                InputLabelProps={{
                  required: true,
                }}
                inputProps={{ maxLength: MAX_LENGTH_TITLE }}
                label={ContentFormsLabels.TITLE}
                margin='normal'
                variant='outlined'
                autoFocus
                className={classes.textFieldLabel}
                error={Boolean(error)}
                helperText={error?.message}
                sx={{ mt: -1, mb: -1 }}
              />
              {value.length >= MAX_LENGTH_TITLE && (
                <Typography
                  variant='input_error'
                  sx={{ mt: 1, pl: 1, backgroundColor: AppColors.BOX_FILL }}
                >
                  {ContentFormsLabels.MAX_TITLE_LENGTH}
                </Typography>
              )}
            </>
          )}
        />

        <Typography variant='subtitle_bold' mt={3} mb={-1}>
          {CommonTexts.DESCRIPTION}
        </Typography>

        <RichTextEditorLexical
          jsonTextEditor={descriptionJSON}
          setJsonTextEditor={changeDescriptionJSON}
          rawHtmlTextEditor={descriptionHTML}
          setRawHtmlTextEditor={changeDescriptionHTML}
          setPlainTextEditor={changeDescriptionPlain}
          resetTextEditorRef={resetDescriptionRef}
          error={descriptionJSONError}
        />
        <Controller
          control={control}
          name='author'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              label={CommonTexts.AUTHOR}
              margin='normal'
              variant='outlined'
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name='link'
          render={({
            field: { value, onChange, onBlur, ref },
            fieldState: { error },
          }) => (
            <TextField
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              inputRef={ref}
              type='text'
              label={ContentFormsLabels.LINK}
              margin='normal'
              variant='outlined'
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name='notificationText'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              label={ContentFormsLabels.NOTIFICATION_TEXT}
              margin='normal'
              variant='outlined'
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message}
              sx={{ mb: -1 }}
            />
          )}
        />

        <Typography variant='subtitle_bold' mt={3} mb={-1}>
          {CommonTexts.NOTIFICATION_TEXT_STYLES}
        </Typography>
        <RichTextEditorLexical
          jsonTextEditor={notificationJSON}
          setJsonTextEditor={changeNotificationJSON}
          rawHtmlTextEditor={notificationHTML}
          setRawHtmlTextEditor={changeNotificationHTML}
          setPlainTextEditor={changeNotificationPlain}
          resetTextEditorRef={resetStyledNotificationRef}
          isNotificationEditor={true}
        />

        <Controller
          control={control}
          name='contentType'
          render={({
            field: { onChange, onBlur, value },
            fieldState: { error },
          }) => (
            <SingleChoiceDropdown
              label={ContentFormsLabels.CONTENT_TYPE}
              labelRequired={true}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              items={dropdownOptions}
              error={error}
            />
          )}
        />

        <Controller
          control={control}
          name='label'
          render={({
            field: { value, onChange, onBlur, ref },
            fieldState: { error },
          }) => (
            <>
              <TextField
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                inputRef={ref}
                type='text'
                label={ContentFormsLabels.LABEL}
                margin='normal'
                variant='outlined'
                className={classes.textFieldLabel}
                inputProps={{ maxLength: MAX_LENGTH_LABEL }}
                error={Boolean(error)}
                helperText={error?.message}
              />
              {value.length >= MAX_LENGTH_LABEL && (
                <Typography
                  variant='input_error'
                  sx={{ mt: -1, pl: 1, backgroundColor: AppColors.BOX_FILL }}
                >
                  {ContentFormsLabels.MAX_LABEL_LENGTH}
                </Typography>
              )}
            </>
          )}
        />

        <Controller
          control={control}
          name='businessPackage'
          render={({ field: { onChange, onBlur, value } }) => (
            <SingleChoiceDropdown
              label={ContentFormsLabels.PACKAGE}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              items={businessPackageOptions}
            />
          )}
        />

        <Controller
          control={control}
          name='packageLabel'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              label={ContentFormsLabels.PACKAGE_LABEL}
              margin='normal'
              variant='outlined'
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message}
            />
          )}
        />
        <FileInput
          label={ContentFormsLabels.IMAGE}
          existingFile={urlImage ? urlImage : image}
          onChange={handleImage}
          reset={resetImage}
          fileType={imageContentType}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mx: 4,
            mb: 2,
          }}
        >
          <Controller
            control={control}
            name='fullScreen'
            render={({ field: { value, onChange } }) => (
              <FormControl>
                <FormLabel
                  id='demo-row-radio-buttons-group-label'
                  className={classes.radioGroupColor}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  {ContentFormsLabels.FULL_SCREEN}
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby='demo-row-radio-buttons-group-label'
                  name='row-radio-buttons-group'
                  value={value}
                  onChange={() => {
                    onChange(Boolean(!value));
                  }}
                  sx={{ ml: 1 }}
                >
                  {fullScreenOptions.map((option) => (
                    <FormControlLabel
                      key={option.label}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name='featured'
            render={({ field: { value, onChange } }) => (
              <FormControl>
                <FormLabel
                  id='demo-row-radio-buttons-group-label'
                  className={classes.radioGroupColor}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  {ContentFormsLabels.FEATURED}
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby='demo-row-radio-buttons-group-label'
                  name='row-radio-buttons-group'
                  value={value}
                  onChange={() => {
                    onChange(Boolean(!value));
                  }}
                  sx={{ ml: 1 }}
                >
                  {featuredOptions.map((option) => (
                    <FormControlLabel
                      key={option.label}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          />
        </Box>
        <Typography mt={-1} variant='body' sx={{ color: 'red' }}>
          {formError && contentTexts.formInputErrors.api_error}
        </Typography>
      </FormMonitoring>
    </CustomDialog>
  );
}

export default ContentForm;
